import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Users, ClipboardText, FileDoc, ArrowRight, DeviceMobile, Coffee, CaretDown, CaretUp, ShieldWarning, HandHeart, CalendarBlank, GraduationCap, PhoneCall, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import QuickNoteCard from "../components/QuickNoteCard";
import ShiftWrapCard from "../components/ShiftWrapCard";
import MyTrainingsCard from "../components/MyTrainingsCard";
import MyCarePlansCard from "../components/MyCarePlansCard";
import { AdminRoster } from "../components/AdminRoster";
import { MyLiveSessions } from "../components/LiveSessionsPanel";
import PatternsCard from "../components/PatternsCard";
import { OfficeRoutineCard } from "../components/OfficeRoutineCard";
import { AttentionCard } from "../components/AttentionCard";
import { ClientRosterCard } from "../components/ClientRosterCard";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { SignaturePad } from "../components/SignaturePad";
import ContactOfficeCard from "../components/ContactOfficeCard";
import { downloadAllAuditPackets } from "../lib/auditDownload";
import CriminalDisclosureCard from "../components/CriminalDisclosureCard";
import DocumentUploadCard from "../components/DocumentUploadCard";
import MyMeetingsCard from "../components/MyMeetingsCard";
import TrainingIncompleteCard from "../components/TrainingIncompleteCard";
import AnniversaryCard from "../components/AnniversaryCard";
import SignatureChecklistCard from "../components/SignatureChecklistCard";
import MissingShiftNotesCard from "../components/MissingShiftNotesCard";
import { TuesdayPrepCard } from "../components/TuesdayPrepCard";

const STATUS = {
  in_progress: { label: "In Progress", cls: "bg-[#D68910]/10 text-[#9a6a0b] border-[#D68910]/40" },
  ready: { label: "Ready to Generate", cls: "bg-primary/10 text-primary border-primary/30" },
  generated: { label: "Plan Generated", cls: "bg-[#27AE60]/10 text-[#1e8449] border-[#27AE60]/30" },
};

function StaffWelcomeCard() {
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("shc-staff-intro") === "1");
  if (dismissed) return null;
  return (
    <div className="mb-6 rounded-2xl border-2 border-primary/30 bg-primary/5 p-6" data-testid="staff-welcome-intro">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-heading text-lg font-semibold text-stone-900">Welcome — this is your app</p>
          <p className="mt-1 text-sm leading-relaxed text-stone-600">
            Everything you need day to day lives here. The four things you'll use most:
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-stone-600">
            <li><strong>This dashboard</strong> — your schedule, your to-dos, upcoming meetings (join by video right here)</li>
            <li><strong>Field Mode</strong> on your phone — snap a document photo, dictate a note after a shift, report a call-out, log a call</li>
            <li><strong>My Profile</strong> — update your info, re-upload anything expiring, request time off</li>
            <li><strong>Contact the office</strong> — the card right below this one: tap to call, email, or reach the overnight line</li>
          </ul>
          <p className="mt-2 text-sm text-stone-600">Anything confusing? Call <strong>800-719-6912</strong> — your success is our success.</p>
        </div>
        <button onClick={() => { localStorage.setItem("shc-staff-intro", "1"); setDismissed(true); }}
          className="shrink-0 rounded-full border border-stone-300 bg-white px-3 py-1 text-xs font-medium text-stone-500 hover:bg-stone-900 hover:text-white"
          data-testid="staff-intro-dismiss">Got it</button>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const [clients, setClients] = useState(null);
  const [digest, setDigest] = useState(null);
  const [myDocs, setMyDocs] = useState(null);
  const [myProfile, setMyProfile] = useState(null);
  const [adminData, setAdminData] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [showOps, setShowOps] = useState(false);
  const [expandedTraining, setExpandedTraining] = useState(null);
  const [openBlocked, setOpenBlocked] = useState("");
  const { user } = useAuth();
  const staffPreview = sessionStorage.getItem("shc_view_staff") === "1";
  const navigate = useNavigate();

  const [ocClient, setOcClient] = useState("");
  const [ocStatus, setOcStatus] = useState("");
  const [ocNote, setOcNote] = useState("");
  const [ocAttest, setOcAttest] = useState(false);
  const [ocSig, setOcSig] = useState(null);
  const [ocOpen, setOcOpen] = useState(false);
  const [ocMine, setOcMine] = useState([]);
  const [ocBusy, setOcBusy] = useState(false);
  const [orient, setOrient] = useState(null);
  const [myTasks, setMyTasks] = useState(null);
  const [nudgeDrafts, setNudgeDrafts] = useState({});
  const [nudgeBusy, setNudgeBusy] = useState("");
  const [interviews, setInterviews] = useState([]);
  const [coOpen, setCoOpen] = useState(false);
  const [coUsers, setCoUsers] = useState(null);
  const [coEmp, setCoEmp] = useState("");
  const [coClient, setCoClient] = useState("");
  const [coDate, setCoDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [coReason, setCoReason] = useState("");
  const [coBusy, setCoBusy] = useState(false);

  const loadMyCheckins = () => api.get("/overnight-checkin/mine").then((r) => setOcMine(r.data.checkins || [])).catch(() => {});

  const reloadCallouts = () => api.get("/callouts").then((r) => setAdminData((cur) => cur ? { ...cur, callouts: r.data } : cur)).catch(() => {});
  const openCallout = async () => {
    setCoOpen(true);
    if (!coUsers) {
      try {
        const r = await api.get("/users");
        setCoUsers((Array.isArray(r.data) ? r.data : r.data.users || []).filter((u) => u.active !== false && (u.role || "staff") !== "admin"));
      } catch { setCoUsers([]); }
    }
  };
  const submitCallout = async () => {
    setCoBusy(true);
    try {
      await api.post("/callouts", { user_id: coEmp, client_name: coClient.trim(), date: coDate, reason: coReason.trim() });
      toast.success("Call-out logged — it's on the to-do list for follow-up");
      setCoOpen(false); setCoEmp(""); setCoClient(""); setCoReason("");
      reloadCallouts();
      api.get("/admin/todo").then((r) => setAdminData((cur) => cur ? { ...cur, todo: r.data } : cur)).catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); }
    setCoBusy(false);
  };
  const resolveCallout = async (id) => {
    try {
      await api.post(`/callouts/${id}/resolve`, {});
      toast.success("Call-out marked as followed up");
      reloadCallouts();
      api.get("/admin/todo").then((r) => setAdminData((cur) => cur ? { ...cur, todo: r.data } : cur)).catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    if (user && (user.role !== "admin" || staffPreview)) {
      api.get("/profile/me").then((r) => { setMyDocs(r.data.documents || {}); setMyProfile(r.data); }).catch(() => setMyDocs({}));
      loadMyCheckins();
      api.get("/orientation").then((r) => setOrient(r.data)).catch(() => {});
      api.get("/my/tasks").then((r) => setMyTasks(r.data.tasks || [])).catch(() => setMyTasks([]));
    }
  }, [user]);

  const punchOvernight = async () => {
    setOcBusy(true);
    try {
      const { data } = await api.post("/overnight-checkin", {
        client_name: ocClient.trim(), client_status: ocStatus, note: ocNote.trim(),
        attested: ocAttest, signature_png: ocSig,
      });
      const c = data.checkin;
      toast.success(`Checked in ${new Date(c.punched_at).toLocaleString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit", month: "short", day: "numeric" })} ET — ${c.in_window ? `${c.window} window ✓` : "outside the 1/3/5am windows"}`);
      setOcClient(""); setOcStatus(""); setOcNote(""); setOcAttest(false); setOcSig(null); setOcOpen(false);
      loadMyCheckins();
    } catch (e) { toast.error(formatApiError(e)); }
    setOcBusy(false);
  };

  useEffect(() => {
    if (user?.role === "admin" && !staffPreview) {
      Promise.all([
        api.get("/admin/data-health"), api.get("/caps"), api.get("/references"), api.get("/audit-log"),
        api.get(`/admin/dashboard-lists${showInactive ? "?include_inactive=1" : ""}`),
        api.get("/admin/todo"),
        api.get("/admin/command-center"),
        api.get("/training/tracker"),
        api.get("/callouts"),
      ]).then(([h, c, r, l, g, t, cc, tr, co]) => setAdminData({
        health: h.data, caps: c.data.summary,
        refsPending: r.data.pending ?? r.data.summary?.pending ?? null,
        activity: (Array.isArray(l.data) ? l.data : []).slice(0, 8),
        lists: g.data,
        todo: t.data,
        cc: cc.data,
        tracker: tr.data,
        callouts: co.data,
      })).catch(() => setAdminData({}));
    }
  }, [user, showInactive]);

  useEffect(() => {
    api.get("/clients")
      .then((r) => setClients(r.data))
      .catch((e) => { toast.error(formatApiError(e)); setClients([]); });
  }, []);

  useEffect(() => {
    if (user?.role === "admin" && !staffPreview) {
      api.get("/admin/daily-digest").then((r) => setDigest(r.data)).catch(() => {});
      api.get("/applicants/interviews").then((r) => setInterviews(r.data.interviews || [])).catch(() => {});
    }
  }, [user]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const firstName = (user?.name || "").split(" ")[0] || "there";

  const stats = {
    total: clients?.length || 0,
    ready: clients?.filter((c) => c.status === "ready").length || 0,
    generated: clients?.filter((c) => c.status === "generated").length || 0,
  };

  const clientsBlock = (
    <div data-testid="clients-section">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">Clients</h1>
          <p className="mt-1 text-sm text-stone-500">Assessment progress and AI care plan generation</p>
        </div>
        {user?.role === "admin" && !staffPreview && (
          <Button onClick={() => navigate("/clients/new")} data-testid="new-client-button"
            className="rounded-full bg-primary text-primary-foreground shadow-sm transition-colors hover:bg-stone-900 active:scale-95">
            <Plus size={16} className="mr-1.5" weight="bold" /> New Client Intake
          </Button>
        )}
      </div>

      <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-3">
        {[
          { icon: Users, label: "Total Clients", value: stats.total, tid: "stat-total-clients" },
          { icon: ClipboardText, label: "Ready to Generate", value: stats.ready, tid: "stat-ready-clients" },
          { icon: FileDoc, label: "Plans Generated", value: stats.generated, tid: "stat-generated-clients" },
        ].map(({ icon: Icon, label, value, tid }) => (
          <div key={tid} className="flex items-center gap-4 rounded-xl border border-stone-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md" data-testid={tid}>
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-secondary text-primary">
              <Icon size={22} />
            </span>
            <div>
              <p className="font-heading text-2xl font-semibold text-stone-900">{value}</p>
              <p className="text-xs text-stone-500">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {digest && digest.staff_flagged > 0 && (
        <div className="mb-8 rounded-xl border border-[#B7791F]/30 bg-white p-5 shadow-sm" data-testid="daily-digest-card">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="flex items-center gap-2.5 font-heading text-sm font-semibold text-stone-900">
              <img src="/brand-daily-care.webp" alt="" aria-hidden="true" loading="lazy" className="h-9 w-9 rounded-lg object-cover" />
              Today's compliance digest
            </p>
            <p className="text-xs text-stone-500" data-testid="digest-summary">
              {digest.staff_flagged} of {digest.staff_total} staff have outstanding items · {digest.items.filter((i) => i.blocking.length > 0).length} blocked
            </p>
          </div>
          <div className="mt-3 space-y-2">
            {digest.items.slice(0, 8).map((i) => (
              <Link key={i.user_id} to={`/team/${i.user_id}`}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-stone-200 px-3 py-2 transition-colors hover:border-primary/40 hover:bg-primary/5"
                data-testid={`digest-item-${i.user_id}`}>
                <span className="text-sm font-medium text-stone-800">{i.name}</span>
                <p className="text-xs text-stone-500">
                  {i.blocking.length > 0 && <span className="mr-2 font-medium text-[#C0392B]">Blocked: {i.blocking.map((b) => b.label).join("; ")}</span>}
                  {i.reminders.length > 0 && <span className="text-[#8a5d14]">{i.reminders.length} reminder{i.reminders.length === 1 ? "" : "s"}</span>}
                </p>
              </Link>
            ))}
            {digest.items.length > 8 && <p className="text-xs text-stone-400">+ {digest.items.length - 8} more — see Team page</p>}
          </div>
        </div>
      )}

      {clients === null ? (
        <div className="flex justify-center py-20"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
      ) : clients.length === 0 ? (
        <div className="overflow-hidden rounded-xl border border-dashed border-stone-300 bg-white text-center" data-testid="empty-clients">
          <img src="/brand-personal-care.webp" alt="" aria-hidden="true" loading="lazy" className="h-36 w-full object-cover object-center" />
          <div className="p-16 pt-10">
          <p className="font-heading text-lg font-semibold text-stone-700">No clients yet</p>
          <p className="mt-1 text-sm text-stone-500">Start with a phone intake to create the first client record and generate their de-identified code.</p>
          {user?.role === "admin" && !staffPreview && (
            <Button onClick={() => navigate("/clients/new")} className="mt-6 rounded-full bg-primary text-primary-foreground transition-colors hover:bg-stone-900 active:scale-95" data-testid="empty-new-client-button">
              <Plus size={16} className="mr-1.5" weight="bold" /> Begin Phone Intake
            </Button>
          )}
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="clients-table">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-stone-200 bg-secondary/60 text-xs uppercase tracking-wide text-stone-500">
                <th className="px-6 py-3.5 font-medium">Client</th>
                <th className="px-6 py-3.5 font-medium">Forms</th>
                <th className="px-6 py-3.5 font-medium">Status</th>
                <th className="px-6 py-3.5" />
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => {
                const s = STATUS[c.status] || STATUS.in_progress;
                return (
                  <tr key={c.id} className="border-b border-stone-100 transition-colors last:border-0 hover:bg-accent/50"
                    data-testid={`client-row-${c.client_code}`}>
                    <td className="px-6 py-4 font-medium text-stone-900">{c.full_name || "—"}</td>
                    <td className="px-6 py-4 text-stone-600">{c.forms_completed}/5</td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1.5">
                        <Badge variant="outline" className={s.cls}>{s.label}</Badge>
                        {c.review_due && <Badge variant="outline" className="bg-[#C0392B]/10 text-[#C0392B] border-[#C0392B]/30" data-testid={`review-due-${c.client_code}`}>Review due — {c.review_reason}</Badge>}
                        {c.significant_change && <Badge variant="outline" className="bg-[#D68910]/10 text-[#9a6a0b] border-[#D68910]/40" data-testid={`sig-change-${c.client_code}`}>Significant change</Badge>}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link to={`/clients/${c.id}`} data-testid={`open-client-${c.client_code}`}
                        className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-stone-900">
                        Open <ArrowRight size={14} />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  if (user?.role === "admin" && !staffPreview) {
    const h = adminData?.health;
    const refsPending = adminData?.refsPending;
    const capsOverdue = adminData?.caps?.overdue;
    const priorities = [
      { label: "Staff with outstanding compliance items", value: digest ? digest.staff_flagged : "—", to: "/users", tid: "priority-staff-flagged" },
      { label: "References awaiting review", value: refsPending ?? "—", to: "/references", tid: "priority-refs-pending" },
      { label: "CAPs with overdue steps", value: capsOverdue ?? "—", to: "/quality", tid: "priority-caps-overdue" },
    ];
    const tiles = h ? [
      ["Active staff", h.staff_active, `${h.staff_total} total records`, "home-staff", "/users"],
      ["Interviews on file", h.interview_records, `${h.staff_with_interview} staff covered`, "home-interviews", "/hiring"],
      ["Satisfactory references", h.references_satisfactory, `${h.references_pending} pending review`, "home-references", "/references"],
      ["IDs on file", h.ids_on_file, `of ${h.staff_total} staff`, "home-ids", "/users"],
      ["Competency records", h.competency_records, "exams + certificates", "home-competency", "/training"],
      ["Knowledge sources", h.kb_sources, `${h.kb_approved} approved`, "home-kb", "/knowledge"],
    ] : [];
    const trainingBehind = (adminData?.tracker?.people || []).filter((p) => p.outstanding.length > 0).length;
    const criticalExpiring = (adminData?.cc?.expiring || []).filter((e) => e.critical).length;
    const openCallouts = (adminData?.callouts?.open || []).length;
    const blockedCount = digest ? digest.items.filter((i) => i.blocking.length > 0).length : 0;
    const openForms = digest?.open_forms || [];
    const actionItems = [
      (digest?.overnight_safety || 0) > 0 && { icon: ShieldWarning, text: `SAFETY: ${digest.overnight_safety} overnight check-in${digest.overnight_safety === 1 ? "" : "s"} missed — call now`, to: "/overnight-review", cta: "Review now", tid: "action-overnight-safety" },
      (digest?.overnight_open || 0) > (digest?.overnight_safety || 0) && { icon: ClipboardText, text: `${digest.overnight_open - (digest.overnight_safety || 0)} administrative overnight follow-up${digest.overnight_open - (digest.overnight_safety || 0) === 1 ? "" : "s"}`, to: "/overnight-review", cta: "Review", tid: "action-overnight-admin" },
      openForms.length > 0 && { icon: ClipboardText, text: `${openForms.length} separation form${openForms.length === 1 ? "" : "s"} unfinished — oldest open ${Math.max(...openForms.map((f) => f.age_days || 0))} day${Math.max(...openForms.map((f) => f.age_days || 0)) === 1 ? "" : "s"} (this nags daily)`, to: "/users", cta: "Finish", tid: "action-separation-forms" },
      digest?.staff_flagged > 0 && { icon: ShieldWarning, text: blockedCount > 0 ? `${blockedCount} staff blocked on a required item · ${digest.staff_flagged} total with record to-dos` : `${digest.staff_flagged} staff have record to-dos (nothing blocking)`, to: "/compliance-directory", cta: "Review", tid: "action-compliance" },
      refsPending > 0 && { icon: Users, text: `${refsPending} reference${refsPending === 1 ? "" : "s"} awaiting review`, to: "/references", cta: "Review", tid: "action-refs" },
      capsOverdue > 0 && { icon: ClipboardText, text: `${capsOverdue} CAP step${capsOverdue === 1 ? "" : "s"} overdue`, to: "/quality", cta: "Resolve", tid: "action-caps" },
      openCallouts > 0 && { icon: PhoneCall, text: `${openCallouts} call-out${openCallouts === 1 ? " needs" : "s need"} follow-up`, to: "/calendar", cta: "Follow up", tid: "action-callouts" },
      trainingBehind > 0 && { icon: GraduationCap, text: `${trainingBehind} staff have outstanding training`, to: "/training", cta: "Review", tid: "action-training" },
      criticalExpiring > 0 && { icon: CalendarBlank, text: `${criticalExpiring} item${criticalExpiring === 1 ? "" : "s"} expiring within 2 weeks`, to: "/calendar", cta: "Review", tid: "action-expiring" },
    ].filter(Boolean).slice(0, 5);
    const momentum = [
      h && { label: `${h.staff_active} active staff`, tid: "momentum-staff" },
      adminData?.cc?.client_status && { label: `${adminData.cc.client_status.active || 0} clients active`, tid: "momentum-clients" },
      adminData?.tracker && trainingBehind === 0 && { label: "Everyone current on required training", tid: "momentum-training" },
      adminData?.cc?.last_hire && { label: `Newest: ${adminData.cc.last_hire.name}`, tid: "momentum-hire" },
    ].filter(Boolean).slice(0, 4);
    return (
      <div data-testid="admin-home">
        {/* Zone 1 — The Briefing: greeting + what's going well + quick note */}
        <div className="mb-8 grid grid-cols-1 items-start gap-6 lg:grid-cols-12">
          <div className="relative flex overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm lg:col-span-8" data-testid="admin-hero-band">
            <div className="flex flex-1 flex-col justify-center px-8 py-8">
              <p className="font-heading text-4xl font-semibold tracking-tight text-[#4A235A] sm:text-5xl">{greeting}, {firstName}</p>
              <p className="mt-2 text-sm text-stone-500">Here's the state of Silver Home Care — the good first.</p>
              {momentum.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2" data-testid="momentum-chips">
                  {momentum.map((m) => (
                    <span key={m.tid} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700" data-testid={m.tid}>
                      <Sparkle size={12} /> {m.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <img src="/brand-companionship.webp" alt="" aria-hidden="true" loading="lazy"
              className="hidden max-h-56 w-2/5 object-cover md:block" />
          </div>
          <div className="lg:col-span-4" data-testid="quick-note-panel"><QuickNoteCard compact /></div>
        </div>

        {/* Zone 2 — Action Center: the few things that actually need you */}
        {user?.role === "admin" && !staffPreview && <AttentionCard />}
        {user?.role === "admin" && !staffPreview && (
          <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-stone-200 bg-white px-4 py-2.5 shadow-sm" data-testid="inspection-logs-strip">
            <span className="text-xs font-semibold uppercase tracking-wide text-stone-400">Inspection logs</span>
            <Link to="/complaints" className="rounded-full bg-[#B7791F]/10 px-3 py-1 text-xs font-semibold text-[#8a5d14] hover:bg-[#B7791F]/20" data-testid="dash-complaints-link">Complaints log</Link>
            <Link to="/incidents" className="rounded-full bg-[#C0392B]/10 px-3 py-1 text-xs font-semibold text-[#C0392B] hover:bg-[#C0392B]/20" data-testid="dash-incidents-link">Incident log</Link>
          </div>
        )}
        {user?.role === "admin" && !staffPreview && <OfficeRoutineCard />}
        {user?.role === "admin" && !staffPreview && <ClientRosterCard />}
        <div className="mb-8 rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="action-center">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-stone-100 px-6 py-4">
            <h2 className="font-heading text-2xl tracking-tight text-[#4A235A]">What needs you today</h2>
            {adminData?.lists?.quarterly_meeting && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs text-stone-500" data-testid="quarterly-meeting-countdown">
                <CalendarBlank size={13} /> Quarterly meeting · {adminData.lists.quarterly_meeting.label} (in {adminData.lists.quarterly_meeting.days_away}d)
              </span>
            )}
          </div>
          {actionItems.length === 0 ? (
            <div className="flex items-center gap-3 px-6 py-8" data-testid="action-center-empty">
              <Coffee size={28} className="text-[#4A235A]" />
              <div>
                  <p className="text-sm font-semibold text-stone-800">All caught up for today.</p>
                  <p className="text-xs text-stone-500">Nothing is blocking. The background detail is one click away below.</p>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-stone-100" data-testid="priority-action-list">
              {actionItems.map((a) => (
                <div key={a.tid} className="group flex items-center justify-between px-6 py-3" data-testid={a.tid}>
                  <span className="flex items-center gap-3 text-sm text-stone-700">
                    <a.icon size={20} className="shrink-0 text-[#4A235A]" />
                    {a.text}
                  </span>
                  <button onClick={() => navigate(a.to)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#4A235A] hover:underline"
                    data-testid={`${a.tid}-resolve-btn`}>
                    {a.cta} <ArrowRight size={12} className="transition-transform duration-200 group-hover:translate-x-1" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Zone 3 — The roster: everyone who has worked, sortable + filterable */}
        <AdminRoster />

        {/* Operations detail — everything else, one click away */}
        <div className="mt-8">
          <button onClick={() => setShowOps((v) => !v)}
            className="flex w-full items-center justify-between rounded-xl border border-stone-200 bg-[#F5F5F0] px-6 py-4 text-left shadow-sm transition-colors duration-200 hover:bg-stone-100"
            data-testid="ops-detail-toggle">
            <span>
              <span className="font-heading text-lg tracking-tight text-stone-800">Operations detail</span>
              <span className="ml-2 text-xs text-stone-500">patterns, to-dos, expirations, call-outs, clients, gap lists, activity</span>
            </span>
            {showOps ? <CaretUp size={18} className="text-stone-400" /> : <CaretDown size={18} className="text-stone-400" />}
          </button>
        </div>
        {showOps && (<>
        <div className="mt-6"><PatternsCard /></div>
        <MissingShiftNotesCard />

        {adminData?.lists?.quarterly_meeting && (
          <div className={`mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4 shadow-sm ${adminData.lists.quarterly_meeting.days_away <= 14 ? "border-[#B7791F]/40 bg-[#B7791F]/5" : "border-stone-200 bg-white"}`} data-testid="quarterly-meeting">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">Quarterly management meeting</p>
              <p className="mt-0.5 text-sm font-semibold text-stone-900" data-testid="quarterly-meeting-date">{adminData.lists.quarterly_meeting.label}</p>
              <p className="text-xs text-stone-400">first Tuesday after quarter end · in {adminData.lists.quarterly_meeting.days_away} day{adminData.lists.quarterly_meeting.days_away === 1 ? "" : "s"}</p>
            </div>
            <span className="flex flex-wrap items-center gap-3">
              <Link to="/quality" className="text-xs font-medium text-primary hover:underline" data-testid="quarterly-meeting-link">Open Quality (QM records)</Link>
              <a href={`${api.defaults.baseURL}/qm-agenda`} target="_blank" rel="noreferrer" className="text-xs font-medium text-primary hover:underline" data-testid="quarterly-meeting-agenda">View meeting agenda (PDF)</a>
            </span>
          </div>
        )}

        <div className="mb-6"><TuesdayPrepCard /></div>

        <div className="relative z-10 mx-2 -mt-10 mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          {priorities.map((p) => (
            <button key={p.tid} onClick={() => navigate(p.to)}
              className="rounded-xl border border-stone-200 bg-white p-8 text-left shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-md focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-testid={p.tid}>
              <p className={`font-heading text-5xl font-bold tracking-tighter ${p.value > 0 ? "text-amber-700" : "text-emerald-700"}`}>{p.value}</p>
              <p className="mt-2 text-xs font-medium uppercase tracking-[0.15em] text-stone-500">{p.label}</p>
            </button>
          ))}
        </div>

        {adminData?.cc && (
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="command-center">
            <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm" data-testid="cc-last-hire">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">Last hire</p>
              <p className="mt-1 text-sm font-semibold text-stone-800">{adminData.cc.last_hire?.name || "—"}</p>
              <p className="text-xs text-stone-400">{adminData.cc.last_hire?.start_date || ""}</p>
              <p className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-stone-400">Next employee ID</p>
              <p className="mt-0.5 text-sm font-semibold text-primary" data-testid="cc-next-id">{adminData.cc.next_employee_id || "—"}</p>
            </div>
            <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm" data-testid="cc-next-event">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">Next on the calendar</p>
              {adminData.cc.next_event ? (
                <>
                  <p className="mt-1 text-sm font-semibold text-stone-800">{adminData.cc.next_event.label}</p>
                  <p className="text-xs text-stone-400">{adminData.cc.next_event.date}</p>
                  <button onClick={() => navigate("/calendar")} className="mt-2 text-xs font-medium text-primary hover:underline" data-testid="cc-calendar-link">Open calendar</button>
                </>
              ) : <p className="mt-1 text-xs text-stone-400">Nothing scheduled.</p>}
            </div>
            <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm" data-testid="cc-expiring">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">Expiring / overdue — next 30 days ({adminData.cc.expiring_count})</p>
              <div className="mt-1.5 max-h-28 space-y-1 overflow-y-auto">
                {adminData.cc.expiring.length === 0 && <p className="text-xs text-[#1e8449]">Nothing expiring in the next 30 days.</p>}
                {adminData.cc.expiring.some((e) => e.critical) && (
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#C0392B]" data-testid="cc-expiring-critical-label">Critical — next 2 weeks</p>
                )}
                {adminData.cc.expiring.filter((e) => e.critical).slice(0, 20).map((e, i) => (
                  <button key={`c${i}`} onClick={() => navigate(e.ref)} className="block w-full text-left" data-testid={`cc-expiring-critical-${i}`}>
                    <span className="text-xs font-semibold text-[#C0392B]">
                      {e.name} — {e.item} · {e.status === "overdue" ? "was due" : "due"} {e.due}
                    </span>
                  </button>
                ))}
                {adminData.cc.expiring.some((e) => !e.critical) && (
                  <p className="pt-1 text-[10px] font-bold uppercase tracking-wider text-stone-400" data-testid="cc-expiring-later-label">Later this month</p>
                )}
                {adminData.cc.expiring.filter((e) => !e.critical).slice(0, 20).map((e, i) => (
                  <button key={`l${i}`} onClick={() => navigate(e.ref)} className="block w-full text-left" data-testid={`cc-expiring-${i}`}>
                    <span className="text-xs text-[#8a5d14]">{e.name} — {e.item} · due {e.due}</span>
                  </button>
                ))}
                {adminData.cc.expiring.length > 40 && <p className="text-[10px] text-stone-400">+{adminData.cc.expiring.length - 40} more</p>}
              </div>
            </div>
            <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm" data-testid="cc-people">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">People</p>
              <p className="mt-1 text-sm font-semibold text-stone-800" data-testid="cc-employee-count">{adminData.cc.employees} active employees</p>
              <p className="mt-0.5 text-xs text-stone-500" data-testid="cc-client-counts">
                Clients: <span className="font-medium text-[#1e8449]">{adminData.cc.client_status?.active || 0} active</span>
                {" · "}<span className="font-medium text-[#8a5d14]">{adminData.cc.client_status?.paused || 0} paused</span>
                {" · "}<span className="font-medium text-stone-500">{adminData.cc.client_status?.inactive || 0} inactive</span>
              </p>
              {adminData.cc.visit_data_stale && (
                <p className="mt-2 rounded-md bg-[#B7791F]/10 px-2 py-1 text-[10px] font-medium text-[#8a5d14]" data-testid="cc-stale-warning">
                  Visit data only goes through {adminData.cc.visit_data_through} — client statuses are frozen until the next visit file is uploaded.
                </p>
              )}
            </div>
          </div>
        )}

        {adminData?.todo && (
          <div className="mb-6 rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="admin-todo-summary">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-heading text-sm font-semibold text-stone-900">
                To-do {adminData.todo.total > 0 ? `— ${adminData.todo.total} item(s) need attention` : "— all clear"}
              </p>
              <button onClick={() => navigate("/calendar")} className="text-xs font-medium text-primary hover:underline" data-testid="admin-todo-view">Open calendar &amp; to-do</button>
            </div>
            {adminData.todo.total > 0 && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="admin-todo-chips">
                {[["callout", "Call-outs"], ["careplan_presign", "Care plan not signed — shift today"], ["note_follow_up", "Note follow-ups"], ["training_overdue", "Trainings overdue"], ["missed_visit", "Missed visits"], ["missed_punch", "Missed punches to sign off"], ["careplan_ack", "Care plan acks"], ["checkin_due", "Check-ins due"], ["training_due", "Trainings due soon"], ["notifications", "Approvals"]]
                  .filter(([k]) => adminData.todo.counts?.[k]).map(([k, label]) => (
                  <button key={k} onClick={() => navigate((adminData.todo.items || []).find((i) => i.kind === k)?.ref || "/calendar")}
                    className={k === "careplan_presign"
                      ? "rounded-full border border-[#C0392B]/40 bg-[#C0392B]/10 px-3 py-1 text-xs font-bold text-[#C0392B] transition-colors hover:bg-[#C0392B]/20"
                      : "rounded-full border border-[#B7791F]/30 bg-[#B7791F]/10 px-3 py-1 text-xs font-medium text-[#8a5d14] transition-colors hover:bg-[#B7791F]/20"}
                    data-testid={`admin-todo-chip-${k}`}>{label}: {adminData.todo.counts[k]}</button>
                ))}
              </div>
            )}
          </div>
        )}

        {adminData?.todo && (adminData.todo.items || []).some((i) => i.kind === "checkin_due") && (
          <div className="mb-6 rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="dash-checkins">
            <p className="font-heading text-sm font-semibold text-stone-900">
              Client check-ins due <span className="text-xs font-normal text-stone-400">— quarterly, counted from each client's intake anchor date</span>
            </p>
            <div className="mt-2 space-y-1">
              {adminData.todo.items.filter((i) => i.kind === "checkin_due").map((i, j) => (
                <button key={j} onClick={() => navigate(i.ref)} className="block w-full rounded-md border border-stone-100 px-2.5 py-1.5 text-left text-xs text-stone-600 transition-colors hover:border-primary/40 hover:bg-primary/5" data-testid={`dash-checkin-${j}`}>{i.label}</button>
              ))}
            </div>
          </div>
        )}

        {adminData?.callouts && (
          <div className="mb-6 rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="dash-callouts">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-heading text-sm font-semibold text-stone-900">
                Call-outs {(adminData.callouts.open || []).length > 0 && <span className="text-[#C0392B]">— {(adminData.callouts.open || []).length} need follow-up</span>}
              </p>
              <Button size="sm" onClick={openCallout} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="report-callout-button">
                <Plus size={14} className="mr-1" /> Report a call out
              </Button>
            </div>
            {(adminData.callouts.open || []).length === 0 ? (
              <p className="mt-2 text-xs text-[#1e8449]" data-testid="callouts-empty">No open call-outs.</p>
            ) : (
              <div className="mt-3 space-y-1.5">
                {(adminData.callouts.open || []).map((co) => (
                  <div key={co.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-stone-100 px-3 py-2" data-testid={`callout-${co.id}`}>
                    <p className="text-xs text-stone-600">
                      <Link to={`/team/${co.user_id}`} className="font-medium text-stone-800 hover:text-primary hover:underline">{co.employee_name}</Link>
                      {" · "}{co.date}{co.client_name ? ` · ${co.client_name}` : ""} — {co.reason}
                    </p>
                    <button onClick={() => resolveCallout(co.id)} className="text-xs font-medium text-primary hover:underline" data-testid={`callout-resolve-${co.id}`}>Mark followed up</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {adminData?.tracker && (
          <div className="mb-6 rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="dash-training-outstanding">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-heading text-sm font-semibold text-stone-900">Outstanding training — by employee</p>
              <button onClick={() => navigate("/training")} className="text-xs font-medium text-primary hover:underline" data-testid="dash-training-open">Open Training Tracker</button>
            </div>
            {(() => {
              const behind = (adminData.tracker.people || []).filter((p) => p.outstanding.length > 0);
              if (behind.length === 0) return <p className="mt-2 text-xs text-[#1e8449]" data-testid="dash-training-clear">Everyone is caught up.</p>;
              return (
                <div className="mt-3 max-h-96 space-y-1 overflow-y-auto">
                  {behind.map((p) => {
                    const expanded = expandedTraining === p.id;
                    return (
                      <div key={p.id} className="rounded-md border border-stone-100" data-testid={`dash-training-person-${p.id}`}>
                        <button onClick={() => setExpandedTraining(expanded ? null : p.id)}
                          className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:border-primary/40 hover:bg-primary/5"
                          data-testid={`dash-training-person-toggle-${p.id}`}>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-xs font-medium text-stone-800">{p.name}</span>
                            <span className="mt-1 block h-1 w-full max-w-40 overflow-hidden rounded-full bg-stone-100">
                              <span className="block h-full rounded-full bg-[#1e8449]" style={{ width: `${Math.round((p.done / Math.max(1, p.total)) * 100)}%` }} />
                            </span>
                          </span>
                          <span className="shrink-0 text-[11px] text-stone-400">{p.done}/{p.total} current</span>
                          <span className="shrink-0 rounded-full bg-[#C0392B]/10 px-2 py-0.5 text-xs font-semibold text-[#C0392B]" data-testid={`dash-training-outstanding-count-${p.id}`}>{p.outstanding.length} outstanding</span>
                          {expanded ? <CaretUp size={12} className="shrink-0 text-stone-400" /> : <CaretDown size={12} className="shrink-0 text-stone-400" />}
                        </button>
                        {expanded && (
                          <div className="space-y-0.5 border-t border-stone-100 px-3 py-2" data-testid={`dash-training-person-items-${p.id}`}>
                            {p.outstanding.map((o) => (
                              <div key={o.id} className="flex items-center justify-between gap-2 text-xs">
                                <span className="text-stone-700">
                                  {o.title}
                                  {o.completed_as && <span className="text-stone-400"> · last done as “{o.completed_as}”</span>}
                                </span>
                                <span className="shrink-0 text-stone-400">{o.renew_by ? `was due ${o.renew_by}` : "due now"}</span>
                              </div>
                            ))}
                            <Link to={`/team/${p.id}`} className="mt-1 inline-block text-[11px] font-medium text-primary hover:underline" data-testid={`dash-training-person-file-${p.id}`}>Open {p.name.split(" ")[0]}'s file →</Link>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}

        {coOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="callout-modal">
            <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
              <h2 className="font-heading text-lg font-semibold text-stone-900">Report a call out</h2>
              <p className="mt-0.5 text-xs text-stone-500">Logs the call-out on the employee's attendance record and adds a follow-up to your to-do list.</p>
              <div className="mt-4 space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-stone-500">Employee *</label>
                  <select value={coEmp} onChange={(e) => setCoEmp(e.target.value)} className="h-9 w-full rounded-md border border-stone-300 bg-white px-2 text-sm" data-testid="callout-employee">
                    <option value="">{coUsers === null ? "Loading staff…" : "Pick employee…"}</option>
                    {(coUsers || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-stone-500">Client</label>
                  <input value={coClient} onChange={(e) => setCoClient(e.target.value)} list="callout-client-list" placeholder="Client name (optional)"
                    className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="callout-client" />
                  <datalist id="callout-client-list">
                    {(clients || []).map((c) => <option key={c.id} value={c.full_name || ""} />)}
                  </datalist>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-stone-500">Date *</label>
                  <input type="date" value={coDate} onChange={(e) => setCoDate(e.target.value)} className="h-9 rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="callout-date" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-stone-500">Reason *</label>
                  <textarea rows={3} value={coReason} onChange={(e) => setCoReason(e.target.value)} placeholder="Who called, when, and why they can't make the visit"
                    className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm" data-testid="callout-reason" />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setCoOpen(false)} className="border-stone-300 text-stone-500" data-testid="callout-cancel">Cancel</Button>
                  <Button onClick={submitCallout} disabled={coBusy || !coEmp || !coDate || !coReason.trim()} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="callout-submit">
                    {coBusy ? "Saving…" : "Log call out"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mb-6 flex flex-wrap gap-2" data-testid="admin-quick-links">
          <Button onClick={() => navigate("/users")} className="rounded-full bg-primary text-primary-foreground shadow-sm transition-colors hover:bg-stone-900 active:scale-95" data-testid="quick-team"><Plus size={15} className="mr-1.5" /> Team / add staff</Button>
          <Button onClick={() => navigate("/audit")} variant="outline" className="rounded-full border-stone-300 transition-colors active:scale-95" data-testid="quick-audit">Audit Prep</Button>
          <Button onClick={() => navigate("/payer-audits")} variant="outline" className="rounded-full border-stone-300 transition-colors active:scale-95" data-testid="quick-payer">Payer Audits</Button>
          <button onClick={downloadAllAuditPackets}
            className="inline-flex h-9 items-center rounded-full border border-stone-300 bg-white px-4 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-900 hover:text-white active:scale-95"
            data-testid="quick-packets">Download all audit packets (ZIP)</button>
        </div>

        {clientsBlock}

        {h && (
          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="admin-health-tiles">
            {tiles.map(([label, val, sub, tid, to]) => (
              <Link key={tid} to={to} className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-md" data-testid={tid} title={`Open ${label.toLowerCase()} evidence`}>
                <p className="font-heading text-3xl font-bold tracking-tight text-stone-900">{val}</p>
                <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-stone-500">{label} →</p>
                <p className="text-[10px] text-stone-400">{sub}</p>
              </Link>
            ))}
          </div>
        )}

        {adminData?.lists && (
          <div className="mb-6" data-testid="admin-gap-lists">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                Compliance gaps by topic — {showInactive ? "all staff (incl. inactive)" : "active staff"}
              </p>
              <label className="flex items-center gap-1.5 text-xs font-medium text-stone-500">
                <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="gap-show-inactive" />
                Show inactive too (until settled)
              </label>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm" data-testid="latest-hire-card">
              <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Latest hire</p>
              {adminData.lists.latest_hire ? (
                <>
                  <Link to={`/team/${adminData.lists.latest_hire.user_id}`} className="mt-1 block text-sm font-medium text-stone-800 hover:text-primary hover:underline" data-testid="latest-hire-name">{adminData.lists.latest_hire.name}</Link>
                  <p className="text-xs text-stone-400">hired {adminData.lists.latest_hire.start_date}{adminData.lists.latest_hire.employee_id ? ` · ${adminData.lists.latest_hire.employee_id}` : ""}</p>
                </>
              ) : <p className="mt-1 text-xs text-stone-400">—</p>}
              <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-stone-500">Last ID issued</p>
              <p className="mt-1 text-sm font-medium text-stone-800" data-testid="last-employee-id">{adminData.lists.last_dcw_code} <span className="text-stone-400">(numeric {adminData.lists.last_numeric_id})</span></p>
              <p className="text-xs text-stone-400">next: {adminData.lists.next_dcw_code} / {adminData.lists.next_numeric_id}</p>
            </div>
            <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="upcoming-interviews">
              <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Upcoming interviews</p>
              {interviews.length === 0 ? (
                <p className="mt-2 text-xs text-stone-400">None scheduled — prescreen new applications in <Link to="/hiring" className="text-primary hover:underline">Hiring</Link>.</p>
              ) : (
                <ul className="mt-2 space-y-1.5">
                  {interviews.slice(0, 5).map((iv) => (
                    <li key={iv.id} className="text-xs text-stone-600" data-testid={`interview-${iv.id}`}>
                      <span className="font-medium text-stone-800">{iv.name}</span> — {iv.interview_at} · {iv.position}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {[["Face-to-face interview missing", adminData.lists.missing_interview, "gap-interview"],
              ["Initial competency missing", adminData.lists.missing_competency, "gap-competency"],
              ["TB screen missing (initial)", adminData.lists.missing_tb, "gap-tb"],
              ["Annual TB screen due", adminData.lists.missing_tb_annual || [], "gap-tb-annual"],
              ["Training incomplete", adminData.lists.missing_training || [], "gap-training"]].map(([title, list, tid]) => (
              <div key={tid} className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm transition-all duration-300 hover:shadow-md" data-testid={tid}>
                <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                  {title} <span className={list.length ? "text-[#C0392B]" : "text-[#1e8449]"}>({list.length})</span>
                </p>
                <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto">
                  {list.map((p) => (
                    <li key={p.user_id} className="flex items-baseline justify-between gap-2">
                      <Link to={`/team/${p.user_id}`} className="text-sm text-stone-700 hover:text-primary hover:underline" data-testid={`${tid}-${p.user_id}`}>{p.name}</Link>
                      {p.due && <span className="shrink-0 text-[10px] font-semibold text-[#C0392B]">due {p.due}</span>}
                    </li>
                  ))}
                  {!list.length && <li className="text-xs font-medium text-[#1e8449]">Everyone covered.</li>}
                </ul>
              </div>
            ))}
            <TrainingIncompleteCard />
            <SignatureChecklistCard />
            <AnniversaryCard />
            </div>
          </div>
        )}

        {adminData?.lists && (
          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="admin-rosterability">
            <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="roster-blocked">
              <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                Rosterability — ineligible now <span className={adminData.lists.roster_blocked?.length ? "text-[#C0392B]" : "text-[#1e8449]"}>({adminData.lists.roster_blocked?.length || 0})</span>
              </p>
              <ul className="mt-2 max-h-40 space-y-1.5 overflow-y-auto">
                {(adminData.lists.roster_blocked || []).map((p) => (
                  <li key={p.user_id} data-testid={`blocked-${p.user_id}`}>
                    <div className="flex items-baseline justify-between gap-2">
                      <button type="button" onClick={() => setOpenBlocked(openBlocked === p.user_id ? "" : p.user_id)}
                        className="text-left text-sm font-medium text-stone-800 hover:text-primary hover:underline" data-testid={`blocked-toggle-${p.user_id}`}>
                        {p.name}
                      </button>
                      <span className="flex shrink-0 items-center gap-2">
                        <span className="text-[10px] font-semibold text-[#C0392B]">{p.issue_count} issue{p.issue_count > 1 ? "s" : ""}</span>
                        <Link to={`/team/${p.user_id}`} className="text-[10px] font-medium text-stone-400 hover:text-primary hover:underline" data-testid={`blocked-profile-${p.user_id}`}>profile →</Link>
                      </span>
                    </div>
                    {openBlocked === p.user_id ? (
                      <ul className="mt-1 space-y-0.5 rounded-md bg-[#C0392B]/5 px-2 py-1.5" data-testid={`blocked-issues-${p.user_id}`}>
                        {(p.issues || p.first_issues || []).map((iss, i) => (
                          <li key={i} className="text-[11px] text-stone-700">• {iss}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-[10px] text-stone-400">{(p.first_issues || []).join(" · ")}{p.issue_count > (p.first_issues || []).length ? ` · +${p.issue_count - (p.first_issues || []).length} more — click the name` : ""}</p>
                    )}
                  </li>
                ))}
                {!(adminData.lists.roster_blocked || []).length && <li className="text-xs font-medium text-[#1e8449]">Everyone eligible.</li>}
              </ul>
            </div>
            <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="roster-blocked-soon">
              <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                Blocking within 7 days <span className={adminData.lists.roster_blocked_soon?.length ? "text-[#B7791F]" : "text-[#1e8449]"}>({adminData.lists.roster_blocked_soon?.length || 0})</span>
              </p>
              <ul className="mt-2 max-h-40 space-y-1.5 overflow-y-auto">
                {(adminData.lists.roster_blocked_soon || []).map((p) => (
                  <li key={p.user_id} data-testid={`blocked-soon-${p.user_id}`}>
                    <Link to={`/team/${p.user_id}`} className="text-sm font-medium text-stone-800 hover:text-primary hover:underline">{p.name}</Link>
                    <p className="text-[10px] text-[#8a5d14]">{(p.items || []).map((i) => `${i.label} — ${i.date}`).join(" · ")}</p>
                  </li>
                ))}
                {!(adminData.lists.roster_blocked_soon || []).length && <li className="text-xs font-medium text-[#1e8449]">Nothing expiring this week.</li>}
              </ul>
            </div>
            <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="evv-problems">
              <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                EVV compliance problems <span className={adminData.lists.evv_problems?.length ? "text-[#C0392B]" : "text-[#1e8449]"}>({adminData.lists.evv_problems?.length || 0})</span>
              </p>
              <ul className="mt-2 max-h-40 space-y-1.5 overflow-y-auto">
                {(adminData.lists.evv_problems || []).map((p) => (
                  <li key={p.user_id} className="flex items-baseline justify-between gap-2" data-testid={`evv-problem-${p.user_id}`}>
                    <Link to={`/team/${p.user_id}`} className="text-sm text-stone-700 hover:text-primary hover:underline">{p.name}</Link>
                    <span className="shrink-0 text-[10px] font-semibold text-[#C0392B]">{p.pct}% · {p.month}</span>
                  </li>
                ))}
                {!(adminData.lists.evv_problems || []).length && <li className="text-xs font-medium text-[#1e8449]">No one below the EVV floor.</li>}
              </ul>
            </div>
          </div>
        )}

        {adminData?.activity?.length > 0 && (
          <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="admin-activity">
            <p className="font-heading text-sm font-semibold text-stone-900">Recent activity</p>
            <ul className="mt-3 space-y-0 border-l-2 border-stone-200">
              {adminData.activity.map((e, i) => (
                <li key={i} className="relative py-1.5 pl-4 text-xs text-stone-500 transition-colors hover:bg-stone-50" data-testid={`activity-${i}`}>
                  <span className="absolute -left-[5px] top-2.5 h-2 w-2 rounded-full bg-primary/60" />
                  <span className="text-stone-400">{String(e.at || "").slice(0, 16).replace("T", " ")}</span> — <span className="font-medium text-stone-700">{e.actor}</span> {e.action}
                </li>
              ))}
            </ul>
          </div>
        )}
        </>)}
        <Link to="/field" className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-lg transition-transform hover:scale-95 md:hidden" data-testid="field-mode-button">
          <DeviceMobile size={18} weight="bold" /> Field mode
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      {user && user.role !== "admin" && myDocs && !myDocs.id_card && (        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#B7791F]/40 bg-[#B7791F]/10 p-5" data-testid="id-missing-banner">
          <div>
            <p className="text-sm font-semibold text-[#7c4a03]">We still need a photo of your ID</p>
            <p className="mt-0.5 text-xs text-[#8a5d14]">Please upload your driver's license or photo ID (and your SS card if you haven't) — it takes a minute from your phone.</p>
          </div>
          <Button onClick={() => navigate("/profile")} className="bg-[#B7791F] text-white hover:bg-[#8a5d14]" data-testid="id-missing-upload-button">Upload now</Button>
        </div>
      )}
      {user && user.role === "staff" && user.availability_stale && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#B7791F]/40 bg-[#B7791F]/10 p-5" data-testid="availability-stale-banner">
          <div>
            <p className="text-sm font-semibold text-[#7c4a03]">Please confirm your availability</p>
            <p className="mt-0.5 text-xs text-[#8a5d14]">Every 90 days we ask you to confirm the days and times you can work — it's how we schedule your shifts. Takes 30 seconds.</p>
          </div>
          <Button onClick={() => navigate("/profile")} className="bg-[#B7791F] text-white hover:bg-[#8a5d14]" data-testid="availability-review-button">Review availability</Button>
        </div>
      )}
      {user && user.role === "staff" && user.dedicated_caregiver && user.legal_rep_attest_stale && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#4A235A]/30 bg-[#4A235A]/5 p-5" data-testid="legal-rep-attest-banner">
          <div>
            <p className="text-sm font-semibold text-[#4A235A]">Your annual designated caregiver attestation is due</p>
            <p className="mt-0.5 text-xs text-stone-600">Once a year we ask designated caregivers to re-confirm in writing that they're not the client's legal representative — you already know this rule; this is just our written record. Takes under a minute.</p>
          </div>
          <Button onClick={() => navigate("/profile")} className="bg-[#4A235A] text-white hover:bg-[#381a45]" data-testid="legal-rep-attest-button">Re-sign now</Button>
        </div>
      )}
      {user && (user.role !== "admin" || staffPreview) && <MyTrainingsCard />}
      {user && (user.role !== "admin" || staffPreview) && <MyCarePlansCard />}
      {user && (user.role !== "admin" || staffPreview) && <MyLiveSessions />}
      {user && (user.role !== "admin" || staffPreview) && <ShiftWrapCard />}
      {user && (user.role !== "admin" || staffPreview) && myTasks && myTasks.length > 0 && (
        <div className="mb-6 overflow-hidden rounded-xl border border-[#C0392B]/30 bg-white shadow-sm" data-testid="my-tasks-card">
          <div className="border-b border-stone-100 px-4 py-3">
            <p className="font-heading text-sm font-semibold text-stone-900">Things to complete <span className="text-xs font-normal text-[#C0392B]">— {myTasks.length} open</span></p>
          </div>
          <div className="divide-y divide-stone-50">
            {myTasks.map((t) => (
              t.kind === "code_nudge" ? (
                <div key={t.id} className="px-4 py-2.5" data-testid={`my-task-${t.id}`}>
                  <p className="text-sm font-medium text-stone-800">{t.title}</p>
                  {t.note ? <p className="mt-0.5 text-xs text-stone-500">{t.note}</p> : null}
                  <div className="mt-1.5 flex gap-2">
                    <input value={nudgeDrafts[t.nudge_id] || ""}
                      onChange={(e) => setNudgeDrafts({ ...nudgeDrafts, [t.nudge_id]: e.target.value })}
                      placeholder="Your answer for the office…"
                      className="h-8 flex-1 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid={`nudge-answer-${t.nudge_id}`} />
                    <button disabled={nudgeBusy === t.nudge_id || !(nudgeDrafts[t.nudge_id] || "").trim()}
                      onClick={async () => {
                        setNudgeBusy(t.nudge_id);
                        try {
                          await api.post(`/my/code-nudges/${t.nudge_id}/answer`, { answer: nudgeDrafts[t.nudge_id] });
                          toast.success("Answer sent to the office");
                          setMyTasks((cur) => (cur || []).filter((x) => x.id !== t.id));
                        } catch (err) { toast.error(formatApiError(err)); }
                        setNudgeBusy("");
                      }}
                      className="h-8 rounded-full bg-primary px-3 text-xs font-semibold text-primary-foreground hover:bg-stone-900 disabled:opacity-40" data-testid={`nudge-send-${t.nudge_id}`}>
                      {nudgeBusy === t.nudge_id ? "Sending…" : "Send answer"}
                    </button>
                  </div>
                </div>
              ) : (
              <button key={t.id} onClick={() => navigate((t.key || "").startsWith("sign-plan:") && t.client_id ? `/my-careplan/${t.client_id}` : `/employee-files/${myProfile?.id || ""}`)}
                className="block w-full px-4 py-2.5 text-left transition-colors hover:bg-primary/5" data-testid={`my-task-${t.id}`}>
                <p className="text-sm font-medium text-stone-800">{t.title}{t.due_date ? <span className="ml-2 text-xs font-bold text-[#C0392B]">due {t.due_date}</span> : null}</p>
                {t.note ? <p className="mt-0.5 text-xs text-stone-500">{t.note}</p> : null}
              </button>
              )
            ))}
          </div>
        </div>
      )}
      {user && (user.role !== "admin" || staffPreview) && orient && (() => {
        const steps = (orient.steps || []).filter((s) => s.kind === "curriculum");
        const done = steps.filter((s) => s.done_at).length;
        const pct = steps.length ? Math.round((done / steps.length) * 100) : 0;
        return (
          <div className="mb-6 grid gap-4 sm:grid-cols-2">
            <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="orientation-progress-card">
              <img src="/brand-orientation.webp" alt="" aria-hidden="true" loading="lazy" className="h-24 w-full object-cover object-center" />
              <div className="p-6">
              <div className="flex items-center justify-between">
                <p className="font-heading text-sm font-semibold text-stone-900">My orientation</p>
                <p className="font-heading text-xl font-bold text-primary">{pct}%</p>
              </div>
              <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-stone-100">
                <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
              </div>
              <p className="mt-2 text-xs text-stone-500">{done} of {steps.length} steps complete{orient.orientation_ack ? " · handbook acknowledged" : ""}</p>
              <Button size="sm" onClick={() => navigate("/orientation")} className="mt-3 rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="orientation-continue-button">
                {pct >= 100 ? "Review orientation" : "Continue orientation"}
              </Button>
              </div>
            </div>
            <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="my-profile-card">
              <p className="flex items-center gap-2.5 font-heading text-sm font-semibold text-stone-900">
                <img src="/brand-ambulation.webp" alt="" aria-hidden="true" loading="lazy" className="h-9 w-9 rounded-lg object-cover" />
                My profile &amp; documents
              </p>
              <p className="mt-1 text-xs text-stone-500">Keep your info current and upload your ID, TB screen, and credentials here.</p>
              {myProfile && (
                <div className="mt-3 space-y-1 border-t border-stone-100 pt-3 text-sm" data-testid="my-contact-summary">
                  <p className="text-stone-600"><span className="text-stone-400">Phone:</span> <span className="font-medium text-stone-800">{myProfile.profile?.phone || "—"}</span></p>
                  <p className="text-stone-600"><span className="text-stone-400">Email:</span> <span className="font-medium text-stone-800">{myProfile.email || "—"}</span></p>
                  <p className="text-stone-600"><span className="text-stone-400">Address:</span> <span className="font-medium text-stone-800">{myProfile.profile?.address || "—"}</span></p>
                  <p className="text-stone-600"><span className="text-stone-400">Emergency contact:</span> <span className="font-medium text-stone-800">
                    {[myProfile.profile?.emergency_contact_name, myProfile.profile?.emergency_contact_phone].filter(Boolean).join(" · ") || "—"}
                  </span></p>
                  <p className="pt-1 text-xs text-stone-400">Something wrong? Update it on My Profile — the office reviews changes.</p>
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => navigate("/profile")} className="rounded-full border-stone-300" data-testid="my-profile-link">My Profile</Button>
                <Button size="sm" variant="outline" onClick={() => navigate("/forms")} className="rounded-full border-stone-300" data-testid="my-forms-link">Forms</Button>
                <Button size="sm" variant="outline" onClick={() => navigate("/training")} className="rounded-full border-stone-300" data-testid="my-training-link">Training</Button>
              </div>
            </div>
          </div>
        );
      })()}
      {user && (user.role !== "admin" || staffPreview) && (
        <div className="mb-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="staff-resources-card">
          <p className="font-heading text-sm font-semibold text-stone-900">Resources</p>
          <p className="mt-1 text-xs text-stone-500">Handbook, policies, and task guidelines — everything you might need on a visit.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => navigate("/handbook")} className="rounded-full border-stone-300" data-testid="res-handbook">Employee Handbook</Button>
            <Button size="sm" variant="outline" onClick={() => navigate("/policies")} className="rounded-full border-stone-300" data-testid="res-policies">Policies</Button>
            <Button size="sm" variant="outline" onClick={() => navigate("/guidelines")} className="rounded-full border-stone-300" data-testid="res-guidelines">Task Guidelines</Button>
          </div>
        </div>
      )}
      {user && (user.role !== "admin" || staffPreview) && (
        <>
          <CriminalDisclosureCard />
          <DocumentUploadCard />
          <ContactOfficeCard />
          <MyMeetingsCard />
        </>
      )}
      {user && (user.role !== "admin" || staffPreview) && (
        <div className="relative mb-6 overflow-hidden rounded-2xl border border-stone-200 shadow-sm" data-testid="staff-welcome-strip">
          <img src="/brand-companionship.webp" alt="Silver Home Care caregiver sharing a card game with a client" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-900/80 via-stone-800/50 to-transparent" />
          <div className="relative flex h-36 flex-col justify-center px-7">
            <p className="font-heading text-2xl font-semibold tracking-tight text-stone-50">{greeting}, {firstName}</p>
            <p className="mt-0.5 text-xs text-stone-200">Thank you for the care you give — everything you need for today is below.</p>
          </div>
        </div>
      )}
      {user && (user.role !== "admin" || staffPreview) && (
        <div className="mb-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="visit-reporting-card">
          <p className="font-heading text-sm font-semibold text-stone-900">Visit punch & task reporting</p>
          <p className="mt-1 text-xs text-stone-500">Forgot to clock in or out, or need to report task codes for a visit? Submit them here for office review.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" onClick={() => navigate("/missed-punch/new")} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="quick-missed-punch">Report a missed punch</Button>
            <Button size="sm" variant="outline" onClick={() => navigate("/forms")} className="rounded-full border-stone-300" data-testid="quick-task-codes">Report task codes</Button>
          </div>
        </div>
      )}
      {user && (user.role !== "admin" || staffPreview) && orient && (() => {
        const qr = (orient.steps || []).find((s) => s.title === "Quick Reference Sheet");
        if (!qr) return null;
        const lines = qr.content.split("\n").map((l) => l.trim()).filter(Boolean);
        const ci = lines.findIndex((l) => l === "Key Contacts");
        const contacts = ci >= 0 ? lines.slice(ci + 1).filter((l) => /\d{3}[-.\s]?\d{3}/.test(l)).slice(0, 3) : [];
        return (
          <div className="mb-6 rounded-xl border-2 border-primary/30 bg-white p-6 shadow-sm" data-testid="quick-reference-card">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-heading text-sm font-semibold text-stone-900">Caregiver quick reference</p>
                <p className="mt-0.5 text-xs text-stone-500">Call-out chain: Patty → Kelly → 24/7 hotline. Emergencies: 911 first, then the office.</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => navigate("/quick-reference")} className="rounded-full border-stone-300" data-testid="quick-reference-open">
                Open full sheet
              </Button>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="quick-reference-chips">
              {["Clock in/out in HHAeXchange", "Overnight: 1am · 3am · 5am check-ins", "No gifts, money, or POA for clients", "Unsure? Call the office"].map((t) => (
                <span key={t} className="rounded-full bg-accent px-3 py-1 text-xs font-medium text-accent-foreground">{t}</span>
              ))}
            </div>
            {contacts.length > 0 && (
              <div className="mt-3 space-y-1 border-t border-stone-100 pt-3" data-testid="quick-reference-contacts">
                {contacts.map((l, i) => (
                  <p key={i} className="text-xs text-stone-600">
                    {l.split(/(\+?1?[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4})/g).map((part, j) =>
                      /^\+?1?[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/.test(part.trim())
                        ? <a key={j} href={`tel:${part.replace(/\D/g, "")}`} className="font-semibold text-primary hover:underline">{part}</a>
                        : part
                    )}
                  </p>
                ))}
              </div>
            )}
          </div>
        );
      })()}
      {user && (user.role !== "admin" || staffPreview) && (
        <div className="mb-6 rounded-xl border-2 border-primary/30 bg-white p-6 shadow-sm" data-testid="overnight-checkin-card">
          <p className="font-heading text-sm font-semibold text-stone-900">Overnight Employee Check In</p>
          <p className="mt-1 text-xs leading-relaxed text-stone-500">
            Overnight duty (12am–5am): stay awake, alert, and attentive, with a visual check of the client at least every two hours.
            Check in at <span className="font-medium text-stone-700">1am, 3am, and 5am</span> (15-minute window each).
            If you can't check in here for any reason, call <span className="font-medium text-stone-700">267-547-0310</span> to check in verbally.
            If you don't check in, we may call to verify you're awake — answer that call. This backs up, never replaces, your HHAeXchange EVV clock-in.
          </p>
          {!ocOpen ? (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Button onClick={() => { setOcOpen(true); setOcSig(null); setOcAttest(false); }} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="overnight-open">
                Start overnight check-in
              </Button>
              {ocMine.length > 0 && (
                <p className="text-xs text-stone-400" data-testid="overnight-last">
                  Last: {new Date(ocMine[0].punched_at).toLocaleString("en-US", { timeZone: "America/New_York", weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} ET{ocMine[0].window ? ` · ${ocMine[0].window} window ✓` : ""}
                </p>
              )}
            </div>
          ) : (
            <div className="mt-4 space-y-3" data-testid="overnight-form">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-stone-500">Client's first name *</label>
                  <input value={ocClient} onChange={(e) => setOcClient(e.target.value)} className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="overnight-client-input" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-stone-500">Client status *</label>
                  <div className="flex gap-2">
                    {["awake", "asleep"].map((s) => (
                      <button key={s} onClick={() => setOcStatus(s)}
                        className={`rounded-full border px-3 py-1.5 text-xs font-semibold capitalize ${ocStatus === s ? "border-primary/50 bg-primary/10 text-primary" : "border-stone-200 bg-white text-stone-400"}`}
                        data-testid={`overnight-status-${s}`}>{s}</button>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-stone-500">Note (optional)</label>
                <input value={ocNote} onChange={(e) => setOcNote(e.target.value)} className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="overnight-note" />
              </div>
              <label className="flex cursor-pointer items-start gap-2 text-xs text-stone-600" data-testid="overnight-attest-label">
                <input type="checkbox" checked={ocAttest} onChange={(e) => setOcAttest(e.target.checked)} className="mt-0.5" data-testid="overnight-attest" />
                I acknowledge and agree to abide by this policy and attest that I am awake and alert.
              </label>
              <div>
                <p className="mb-1 text-xs font-medium text-stone-500">Signature *</p>
                <SignaturePad onChange={setOcSig} />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setOcOpen(false)} className="border-stone-300 text-stone-500" data-testid="overnight-cancel">Cancel</Button>
                <Button onClick={punchOvernight} disabled={ocBusy || !ocClient.trim() || !ocStatus || !ocAttest || !ocSig} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="overnight-checkin-button">
                  {ocBusy ? "Checking in…" : "Submit check-in"}
                </Button>
              </div>
            </div>
          )}
          {ocMine.length > 0 && (
            <div className="mt-3 space-y-1 border-t border-stone-100 pt-3" data-testid="overnight-mine">
              {ocMine.slice(0, 5).map((c) => (
                <p key={c.id} className="text-xs text-stone-500" data-testid={`overnight-mine-${c.id}`}>
                  {new Date(c.punched_at).toLocaleString("en-US", { timeZone: "America/New_York", weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} ET · {c.client_name} · {c.client_status}{c.window ? ` · ${c.window} window ✓` : " · outside check-in window"}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
      {clients !== null && clients.length > 0 && (
        <div className="mb-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="my-clients-card">
          <p className="font-heading text-sm font-semibold text-stone-900">My assigned clients</p>
          <p className="mt-0.5 text-xs text-stone-500">Only the clients assigned to you appear here.</p>
          <div className="mt-2 divide-y divide-stone-100">
            {clients.map((c) => (
              <Link key={c.id} to={`/clients/${c.id}`} className="flex items-center justify-between py-2.5 text-sm font-medium text-stone-800 hover:text-primary" data-testid={`my-client-${c.client_code}`}>
                {c.full_name || "—"} <ArrowRight size={14} className="text-stone-400" />
              </Link>
            ))}
          </div>
        </div>
      )}
      <Link to="/field" className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-lg transition-transform hover:scale-95 md:hidden" data-testid="field-mode-button">
        <DeviceMobile size={18} weight="bold" /> Field mode
      </Link>
    </div>
  );
}
